import { useRouter } from 'next/router';
import { Command } from 'cmdk';
import { CommandGroupTitle } from './CommandGroupTitle';
import { StyledCommandItem } from './CommandItem';
import type { RouterOutputs } from '~/trpc-extra/api';
import { useMemo } from 'react';

export function CommandTestSuiteGroup({
  testSuites,
  tests,
  projectId,
  closeModal,
}: {
  testSuites: RouterOutputs['testSuite']['listWithLatestRun'];
  tests: RouterOutputs['test']['listTests'];
  projectId: string;
  closeModal: () => void;
}) {
  const router = useRouter();

  const testSuitesWithTests = useMemo(
    () =>
      testSuites?.map((suite) => ({
        ...suite,
        tests: tests
          .filter((test) => test.testSuites?.find((ts) => ts.id === suite.id))
          .map(({ id, name }) => ({ id, name })),
      })),
    [testSuites, tests],
  );

  return (
    <Command.Group>
      <CommandGroupTitle title="Test Suites" />
      {testSuitesWithTests?.map((testSuite) => (
        <StyledCommandItem
          onSelect={async () => {
            closeModal();
            await router.push(`/project/${projectId}/testSuite/${testSuite.id}`);
          }}
          value={testSuite.id}
          keywords={[testSuite.name, ...testSuite.tests.flatMap((test) => [test.name, test.id])]}
          key={testSuite.id}
        >
          <div className="flex flex-col gap-2">
            <div>{testSuite.name}</div>
            {testSuite.tests.length > 0 && (
              <div className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400">
                {testSuite.tests.map((test) => (
                  <div key={test.id}>{test.name}</div>
                ))}
              </div>
            )}
          </div>
          <div className="flex-shrink-0 opacity-0 group-data-[selected=true]:opacity-60">Test Suite</div>
        </StyledCommandItem>
      ))}
    </Command.Group>
  );
}
